<!-- src/views/patients/PatientDetail.vue -->
<template>
  <div class="patient-detail">
    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
      <div class="mt-3">
        <router-link to="/patients" class="btn btn-outline-primary">
          <i class="bi bi-arrow-left me-2"></i>Volver a la lista de pacientes
        </router-link>
      </div>
    </div>

    <div v-else>
      <!-- Back button and actions bar -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <router-link to="/patients" class="btn btn-outline-primary">
          <i class="bi bi-arrow-left me-2"></i>Volver
        </router-link>

        <div>
          <router-link :to="`/appointments/new?patient=${patient.id}`" class="btn btn-outline-primary me-2">
            <i class="bi bi-calendar-plus me-1"></i> Nueva Cita
          </router-link>
          <router-link :to="`/patients/${patient.id}/edit`" class="btn btn-primary">
            <i class="bi bi-pencil me-1"></i> Editar
          </router-link>
        </div>
      </div>

      <!-- Patient information -->
      <div class="row">
        <div class="col-md-4">
          <!-- Patient info card with Ver completo button -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Información del Paciente</h5>
              <button class="btn btn-sm btn-outline-primary" @click="showPatientInfoModal">
                <i class="bi bi-info-circle me-1"></i> Ver completo
              </button>
            </div>
            <div class="card-body">
              <div class="text-center mb-4">
                <div class="avatar-large mx-auto mb-3">
                  {{ getInitials(patient.first_name, patient.last_name) }}
                </div>
                <h2 class="mb-0">{{ patient.first_name }} {{ patient.last_name }}</h2>
                <p class="text-muted" v-if="patient.fecha_nacimiento">
                  {{ formatAge(patient.fecha_nacimiento) }} años
                </p>
              </div>

              <div class="patient-info">
                <div class="info-item" v-if="patient.phone_number">
                  <div class="info-label">
                    <i class="bi bi-telephone me-2"></i> Teléfono
                  </div>
                  <div class="info-value">{{ patient.phone_number }}</div>
                </div>
                <div class="info-item" v-if="patient.fecha_nacimiento">
                  <div class="info-label">
                    <i class="bi bi-calendar me-2"></i> Fecha de nacimiento
                  </div>
                  <div class="info-value">{{ formatDate(patient.fecha_nacimiento) }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">
                    <i class="bi bi-clipboard2-check me-2"></i> Estado del tratamiento
                  </div>
                  <div class="info-value">
                    <span class="badge" :class="getTreatmentStatusClass(patient.treatment_status)">
                      {{ getTreatmentStatusName(patient.treatment_status) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Clinical History Summary Card -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Historia Clínica</h5>
              <button v-if="hasClinicalHistory" class="btn btn-sm btn-outline-primary" @click="showClinicalHistory">
                Ver completa
              </button>
              <button v-else class="btn btn-sm btn-primary" @click="createClinicalHistory">
                <i class="bi bi-plus-circle me-1"></i> Crear
              </button>
            </div>
            <div class="card-body">
              <div v-if="hasClinicalHistory">
                <!-- Treatment History -->
                <div v-if="treatmentHistory.length > 0" class="mb-3">
                  <h6 class="mb-2">Historial de Tratamientos</h6>
                  <div class="treatment-timeline">
                    <div v-for="(treatment, index) in treatmentHistory.slice(0, 3)" :key="index" class="treatment-item">
                      <div class="treatment-date">{{ formatDate(treatment.date) }}</div>
                      <div class="treatment-content">
                        <h6 class="mb-2">{{ treatment.title }}</h6>
                        <!-- Se muestra línea por línea, respetando líneas vacías -->
                        <p v-for="(line, lineIdx) in getTextLines(treatment.description)" :key="lineIdx" class="mb-1">
                          {{ line || '\u00A0' }}
                        </p>
                        <div class="text-muted small" v-if="treatment.doctor">{{ treatment.doctor }}</div>
                      </div>
                    </div>
                    <div v-if="treatmentHistory.length > 3" class="text-center mt-2">
                      <small class="text-muted">Mostrando 3 de {{ treatmentHistory.length }} tratamientos</small>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted py-3">
                <i class="bi bi-journal-medical d-block display-4 mb-3"></i>
                <p>No hay historial clínico disponible.</p>
              </div>
            </div>
          </div>

          <!-- Treatment Plans Card -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Planes de Tratamiento</h5>
              <div>
                <button v-if="hasTreatmentPlans" class="btn btn-sm btn-outline-primary" @click="viewTreatmentPlans">
                  Ver completo
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="hasTreatmentPlans">
                <div v-for="(plan, index) in patient.treatment_plans.slice(0, 1)" :key="plan.id" class="treatment-plan-item">
                  <div class="mb-3">
                    <span class="text-muted">Plan de tratamiento:</span>
                    <div class="treatment-plan-description">
                      <pre class="treatment-plan-description-text">{{ plan.treatment_plan || 'Sin plan de tratamiento' }}</pre>
                    </div>
                  </div>
                </div>
                <div v-if="patient.treatment_plans.length > 1" class="text-center mt-2">
                  <small class="text-muted">
                    Mostrando 1 de {{ patient.treatment_plans.length }} planes de tratamiento
                  </small>
                </div>
              </div>
              <div v-else class="text-center text-muted py-3">
                <i class="bi bi-clipboard2-pulse d-block display-4 mb-3"></i>
                <p>No hay planes de tratamiento disponibles.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-8">
          <!-- Appointments Card -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Citas</h5>
              <router-link :to="`/appointments/new?patient=${patient.id}`" class="btn btn-sm btn-primary">
                <i class="bi bi-plus-circle me-1"></i> Nueva Cita
              </router-link>
            </div>
            <div class="card-body p-0">
              <div v-if="appointments.length === 0" class="text-center text-muted py-4">
                <i class="bi bi-calendar d-block display-4 mb-3"></i>
                <p>No hay citas registradas para este paciente.</p>
              </div>
              <table v-else class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Hora</th>
                    <th>Tratamiento</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="appointment in appointments" :key="appointment.id">
                    <td>{{ formatDate(appointment.fecha) }}</td>
                    <td>{{ formatTime(appointment.hora) }}</td>
                    <td>{{ appointment.tipo_tratamiento }}</td>
                    <td>
                      <span :class="getStatusClass(appointment.estado)" class="badge rounded-pill">
                        {{ appointment.estado }}
                      </span>
                    </td>
                    <td>
                      <router-link :to="`/appointments/${appointment.id}`" class="btn btn-sm btn-outline-primary me-1">
                        <i class="bi bi-eye"></i>
                      </router-link>
                      <router-link :to="`/appointments/${appointment.id}/edit`"
                        class="btn btn-sm btn-outline-secondary">
                        <i class="bi bi-pencil"></i>
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="appointments.length > 0" class="card-footer bg-white">
              <router-link :to="`/appointments?patient=${patient.id}`" class="btn btn-sm btn-outline-primary">
                Ver todas las citas
              </router-link>
            </div>
          </div>

          <!-- Odontogram Card -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Odontograma</h5>
              <div>
                <button v-if="hasOdontogram" class="btn btn-sm btn-outline-primary me-2" @click="showOdontogram">
                  Ver completo
                </button>
                <button v-if="hasOdontogram" class="btn btn-sm btn-outline-secondary" @click="editOdontogram">
                  <i class="bi bi-pencil"></i> Editar
                </button>
                <button v-else class="btn btn-sm btn-primary" @click="createOdontogram">
                  <i class="bi bi-plus-circle me-1"></i> Crear
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="hasOdontogram">
                <!-- Se usa el componente OdontogramPreview -->
                <OdontogramPreview :odontogramId="odontogram.id" />
              </div>
              <div v-else class="text-center text-muted py-3">
                <i class="bi bi-grid-3x3 d-block display-4 mb-3"></i>
                <p>No hay odontograma registrado para este paciente.</p>
              </div>
            </div>
          </div>

          <!-- Budget Card -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Presupuesto</h5>
              <button class="btn btn-sm btn-primary" @click="showBudgetModal">
                <i class="bi bi-plus-circle me-1"></i> Nuevo Presupuesto
              </button>
            </div>
            <div class="card-body p-0">
              <div v-if="!hasBudgets" class="text-center py-4">
                <i class="bi bi-cash-coin d-block display-4 mb-3 text-muted"></i>
                <p class="text-muted">No hay presupuestos registrados para este paciente.</p>
                <button class="btn btn-primary" @click="showBudgetModal">
                  <i class="bi bi-plus-circle me-1"></i> Crear Presupuesto
                </button>
              </div>
              <div v-else class="list-group list-group-flush">
                <div v-for="budget in patient.budgets" :key="budget.id" class="list-group-item">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 class="mb-1">{{ budget.nombre || `Presupuesto #${budget.id}` }}</h6>
                      <div class="text-muted small">
                        Creado: {{ formatDate(budget.created_at) }}
                      </div>
                    </div>
                    <div class="badge bg-primary rounded-pill">
                      ${{ formatCurrency(budget.total_amount) }}
                    </div>
                  </div>
                  
                  <div class="mb-3" v-if="budget.description">
                    <div class="budget-description">
                      <pre class="budget-description-text">{{ budget.description }}</pre>
                    </div>
                  </div>
                  
                  <div class="mb-2">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <span class="small text-muted">Progreso de pago: {{ budget.payment_progress }}%</span>
                      <span class="small fw-bold">${{ formatCurrency(budget.total_paid) }} / ${{ formatCurrency(budget.total_amount) }}</span>
                    </div>
                    <div class="progress" style="height: 8px;">
                      <div 
                        class="progress-bar progress-bar-striped" 
                        role="progressbar" 
                        :style="{ width: `${budget.payment_progress}%` }" 
                        :class="getProgressBarColor(budget.payment_progress)"
                        :aria-valuenow="budget.payment_progress" 
                        aria-valuemin="0" 
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </div>
                  
                  <div class="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <span v-if="budget.remaining_balance > 0" class="text-danger fw-bold">
                        Saldo: ${{ formatCurrency(budget.remaining_balance) }}
                      </span>
                      <span v-else class="text-success fw-bold">
                        Pago Completado
                      </span>
                    </div>
                    <div>
                      <button class="btn btn-sm btn-outline-primary me-1" @click="showBudgetPayments(budget)">
                        <i class="bi bi-list me-1"></i> Pagos
                      </button>
                      <button class="btn btn-sm btn-primary me-1" @click="registerPaymentForBudget(budget)" v-if="budget.remaining_balance > 0">
                        <i class="bi bi-cash me-1"></i> Pagar
                      </button>
                      <button class="btn btn-sm btn-outline-secondary me-1" @click="editBudget(budget)">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteBudget(budget)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> <!-- Fin row principal -->
    </div>
    
    <!-- Patient Information Modal -->
    <div class="modal fade" :class="{ 'show d-block': showPatientInfoModalFlag }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Información Completa del Paciente</h5>
            <button type="button" class="btn-close" @click="closePatientInfoModal"></button>
          </div>
          <div class="modal-body">
            <!-- Patient Name and Status -->
            <div class="d-flex align-items-center mb-4">
              <div class="avatar-medium me-3">
                {{ getInitials(patient.first_name, patient.last_name) }}
              </div>
              <div>
                <h4 class="mb-0">{{ patient.first_name }} {{ patient.last_name }}</h4>
                <span class="badge mt-1" :class="getTreatmentStatusClass(patient.treatment_status)">
                  {{ getTreatmentStatusName(patient.treatment_status) }}
                </span>
              </div>
            </div>

            <div class="row gx-4">
              <!-- Personal Data Section -->
              <div class="col-md-6">
                <div class="patient-info-section">
                  <h6 class="section-title">Datos Personales</h6>
                  
                  <div class="patient-info-item">
                    <div class="info-icon">
                      <i class="bi bi-calendar"></i>
                    </div>
                    <div class="info-details">
                      <div class="info-label">Fecha de Nacimiento:</div>
                      <div class="info-value">{{ formatDate(patient.fecha_nacimiento) }} ({{ formatAge(patient.fecha_nacimiento) }} años)</div>
                    </div>
                  </div>
                  
                  <div class="patient-info-item">
                    <div class="info-icon">
                      <i class="bi bi-gender-ambiguous"></i>
                    </div>
                    <div class="info-details">
                      <div class="info-label">Género:</div>
                      <div class="info-value text-capitalize">{{ patient.gender || 'No especificado' }}</div>
                    </div>
                  </div>
                  
                  <div class="patient-info-item">
                    <div class="info-icon">
                      <i class="bi bi-card-text"></i>
                    </div>
                    <div class="info-details">
                      <div class="info-label">Cédula de Identidad:</div>
                      <div class="info-value">{{ patient.identity_card || 'No registrada' }}</div>
                    </div>
                  </div>
                  
                  <div class="patient-info-item">
                    <div class="info-icon">
                      <i class="bi bi-briefcase"></i>
                    </div>
                    <div class="info-details">
                      <div class="info-label">Ocupación:</div>
                      <div class="info-value">{{ patient.occupation || 'No especificada' }}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Contact Information Section -->
              <div class="col-md-6">
                <div class="patient-info-section">
                  <h6 class="section-title">Información de Contacto</h6>
                  
                  <div class="patient-info-item">
                    <div class="info-icon">
                      <i class="bi bi-telephone"></i>
                    </div>
                    <div class="info-details">
                      <div class="info-label">Teléfono:</div>
                      <div class="info-value">{{ patient.phone_number || 'No registrado' }}</div>
                    </div>
                  </div>
                  
                  <div class="patient-info-item">
                    <div class="info-icon">
                      <i class="bi bi-envelope"></i>
                    </div>
                    <div class="info-details">
                      <div class="info-label">Email:</div>
                      <div class="info-value">{{ patient.email || 'No registrado' }}</div>
                    </div>
                  </div>
                  
                  <div class="patient-info-item">
                    <div class="info-icon">
                      <i class="bi bi-geo-alt"></i>
                    </div>
                    <div class="info-details">
                      <div class="info-label">Dirección:</div>
                      <div class="info-value">{{ patient.address || 'No registrada' }}</div>
                    </div>
                  </div>
                  
                  <div class="patient-info-item">
                    <div class="info-icon">
                      <i class="bi bi-person-badge"></i>
                    </div>
                    <div class="info-details">
                      <div class="info-label">Referido por:</div>
                      <div class="info-value">{{ patient.referred_by || 'No especificado' }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Additional Notes Section -->
            <div class="patient-info-section mt-4">
              <h6 class="section-title">Notas Adicionales</h6>
              
              <div v-if="patient.patient_notes" class="notes-container mt-3">
                <pre class="notes-content">{{ patient.patient_notes }}</pre>
              </div>
              <div v-else class="text-muted mt-2">
                No hay notas adicionales
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePatientInfoModal">Cerrar</button>
            <router-link :to="`/patients/${patient.id}/edit`" class="btn btn-primary">
              <i class="bi bi-pencil me-1"></i> Editar Paciente
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div class="modal fade" :class="{ 'show d-block': showImageModalFlag }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" v-if="selectedImage">
              {{ getImageTypeLabel(selectedImage.image_type) }}
            </h5>
            <button type="button" class="btn-close" @click="closeImageModal"></button>
          </div>
          <div class="modal-body text-center">
            <img v-if="selectedImage" :src="selectedImage.image" class="img-fluid"
              :alt="selectedImage.description || 'Imagen clínica'">
            <p v-if="selectedImage && selectedImage.description" class="mt-3">
              {{ selectedImage.description }}
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeImageModal">Cerrar</button>
            <button type="button" class="btn btn-primary" @click="showAllImages">
              Ver todas las imágenes
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Budget Modal -->
    <div class="modal fade" :class="{ 'show d-block': showBudgetModalFlag }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingBudget ? 'Editar Presupuesto' : 'Nuevo Presupuesto' }}</h5>
            <button type="button" class="btn-close" @click="closeBudgetModal"></button>
          </div>
          <div class="modal-body">
            <form id="budgetForm" @submit.prevent="saveBudget">
              <div class="mb-3">
                <label for="nombre" class="form-label">Nombre*</label>
                <input type="text" class="form-control" id="nombre" v-model="budgetForm.nombre"
                  placeholder="Nombre del presupuesto" required>
                <div v-if="budgetValidationErrors.nombre" class="text-danger mt-1 small">
                  {{ budgetValidationErrors.nombre }}
                </div>
              </div>

              <div class="mb-3">
                <label for="total_amount" class="form-label">Monto Total*</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="total_amount" v-model="budgetForm.total_amount"
                    step="0.01" min="0.01" required>
                </div>
                <div v-if="budgetValidationErrors.total_amount" class="text-danger mt-1 small">
                  {{ budgetValidationErrors.total_amount }}
                </div>
              </div>

              <div class="mb-3">
                <label for="budget_description" class="form-label">Descripción</label>
                <textarea class="form-control" id="budget_description" rows="3" v-model="budgetForm.description"
                  placeholder="Detalles adicionales del presupuesto..."></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeBudgetModal">Cancelar</button>
            <button type="submit" class="btn btn-primary" form="budgetForm" :disabled="savingBudget">
              <span v-if="savingBudget" class="spinner-border spinner-border-sm me-2" role="status"
                aria-hidden="true"></span>
              {{ editingBudget ? 'Actualizar' : 'Guardar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Modal -->
    <div class="modal fade" :class="{ 'show d-block': showPaymentModalFlag }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Registrar pago</h5>
            <button type="button" class="btn-close" @click="closePaymentModal"></button>
          </div>
          <div class="modal-body">
            <form id="paymentForm" @submit.prevent="savePayment">
              <div class="mb-3">
                <label for="amount" class="form-label">Monto*</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="amount" v-model="newPayment.amount" step="0.01"
                    min="0.01" :max="selectedBudget?.remaining_balance > 0 ? selectedBudget.remaining_balance : null"
                    required>
                </div>
                <div class="form-text">Saldo pendiente: ${{ formatCurrency(selectedBudget?.remaining_balance || 0) }}</div>
              </div>

              <div class="mb-3">
                <label for="payment_date" class="form-label">Fecha de pago*</label>
                <input type="date" class="form-control" id="payment_date" v-model="newPayment.payment_date" required>
              </div>

              <div class="mb-3">
                <label for="payment_method" class="form-label">Método de pago*</label>
                <select class="form-select" id="payment_method" v-model="newPayment.payment_method" required>
                  <option value="">Seleccionar método de pago</option>
                  <option value="CASH">Efectivo</option>
                  <option value="CARD">Tarjeta</option>
                  <option value="TRANSFER">Transferencia</option>
                  <option value="CHECK">Cheque</option>
                  <option value="OTHER">Otro</option>
                </select>
              </div>

              <div class="mb-3">
                <label for="budget" class="form-label">Presupuesto*</label>
                <select class="form-select" id="budget" v-model="newPayment.budget" required>
                  <option value="">Seleccionar presupuesto</option>
                  <option v-for="budget in patient.budgets" :key="budget.id" :value="budget.id">
                    {{ budget.nombre || `Presupuesto #${budget.id}` }} - ${{ formatCurrency(budget.total_amount) }}
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label for="notes" class="form-label">Notas</label>
                <textarea class="form-control" id="notes" rows="2" v-model="newPayment.notes"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePaymentModal">Cancelar</button>
            <button type="submit" class="btn btn-primary" form="paymentForm">Guardar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Payment Modal -->
    <div class="modal fade" :class="{ 'show d-block': showEditPaymentModalFlag }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Editar pago</h5>
            <button type="button" class="btn-close" @click="closeEditPaymentModal"></button>
          </div>
          <div class="modal-body">
            <form id="editPaymentForm" @submit.prevent="updatePayment">
              <div class="mb-3">
                <label for="edit_amount" class="form-label">Monto*</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" id="edit_amount" v-model="editPayment.amount" step="0.01"
                    min="0.01" required>
                </div>
              </div>

              <div class="mb-3">
                <label for="edit_payment_date" class="form-label">Fecha de pago*</label>
                <input type="date" class="form-control" id="edit_payment_date" v-model="editPayment.payment_date"
                  required>
              </div>

              <div class="mb-3">
                <label for="edit_payment_method" class="form-label">Método de pago*</label>
                <select class="form-select" id="edit_payment_method" v-model="editPayment.payment_method" required>
                  <option value="">Seleccionar método de pago</option>
                  <option value="CASH">Efectivo</option>
                  <option value="CARD">Tarjeta</option>
                  <option value="TRANSFER">Transferencia</option>
                  <option value="CHECK">Cheque</option>
                  <option value="OTHER">Otro</option>
                </select>
              </div>

              <div class="mb-3">
                <label for="edit_budget" class="form-label">Presupuesto*</label>
                <select class="form-select" id="edit_budget" v-model="editPayment.budget" required>
                  <option value="">Seleccionar presupuesto</option>
                  <option v-for="budget in patient.budgets" :key="budget.id" :value="budget.id">
                    {{ budget.nombre || `Presupuesto #${budget.id}` }} - ${{ formatCurrency(budget.total_amount) }}
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label for="edit_notes" class="form-label">Notas</label>
                <textarea class="form-control" id="edit_notes" rows="2" v-model="editPayment.notes"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditPaymentModal">Cancelar</button>
            <button type="submit" class="btn btn-primary" form="editPaymentForm">Actualizar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment History Modal -->
    <div class="modal fade" :class="{ 'show d-block': showPaymentHistoryModalFlag }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Historial de pagos</h5>
            <button type="button" class="btn-close" @click="closePaymentHistoryModal"></button>
          </div>
          <div class="modal-body p-0">
            <div v-if="payments.length === 0" class="text-center p-4">
              <i class="bi bi-cash-stack display-4 text-muted mb-3"></i>
              <p>No hay pagos registrados para este paciente.</p>
            </div>
            <table v-else class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Método</th>
                  <th>Presupuesto</th>
                  <th>Notas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="payment in filteredPayments" :key="payment.id">
                  <td>{{ formatDate(payment.payment_date) }}</td>
                  <td>${{ formatCurrency(payment.amount) }}</td>
                  <td>{{ getPaymentMethodLabel(payment.payment_method) }}</td>
                  <td>{{ getBudgetDescription(payment.budget) }}</td>
                  <td>{{ payment.notes || '-' }}</td>
                  <td>
                    <button class="btn btn-sm btn-outline-secondary me-1" @click="openEditPaymentModal(payment)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" @click="openDeletePaymentModal(payment)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-light fw-bold">
                  <td>Total:</td>
                  <td>${{ formatCurrency(getTotalFilteredPayments()) }}</td>
                  <td colspan="4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="modal-footer">
            <div class="me-auto" v-if="hasBudgets && patient.budgets.length > 1">
              <select class="form-select form-select-sm" v-model="paymentFilterBudgetId">
                <option value="all">Todos los presupuestos</option>
                <option v-for="budget in patient.budgets" :key="budget.id" :value="budget.id">
                  {{ budget.nombre || `Presupuesto #${budget.id}` }}
                </option>
              </select>
            </div>
            <button type="button" class="btn btn-secondary" @click="closePaymentHistoryModal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Payment Confirmation Modal -->
    <div class="modal fade" :class="{ 'show d-block': showDeletePaymentModalFlag }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar eliminación</h5>
            <button type="button" class="btn-close" @click="closeDeletePaymentModal"></button>
          </div>
          <div class="modal-body">
            <p>¿Está seguro de que desea eliminar este pago?</p>
            <div v-if="selectedPayment" class="alert alert-info">
              <strong>Fecha:</strong> {{ formatDate(selectedPayment.payment_date) }}<br>
              <strong>Monto:</strong> ${{ formatCurrency(selectedPayment.amount) }}<br>
              <strong>Método:</strong> {{ getPaymentMethodLabel(selectedPayment.payment_method) }}
            </div>
            <p class="text-danger">Esta acción no se puede deshacer.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDeletePaymentModal">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="deletePayment" :disabled="deletingBudget">
              <span v-if="deletingBudget" class="spinner-border spinner-border-sm me-2" role="status"
                aria-hidden="true"></span>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Budget Payments Modal -->
    <div class="modal fade" :class="{ 'show d-block': showBudgetPaymentsModal }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Pagos del Presupuesto</h5>
            <button type="button" class="btn-close" @click="closeBudgetPaymentsModal"></button>
          </div>
          <div class="modal-body p-0">
            <div v-if="selectedBudget" class="p-3 bg-light border-bottom">
              <div class="row">
                <div class="col-md-6">
                  <h6>{{ selectedBudget.nombre || `Presupuesto #${selectedBudget.id}` }}</h6>
                  <div v-if="selectedBudget.description" class="budget-description mt-2 mb-2">
                    <pre class="budget-description-text">{{ selectedBudget.description }}</pre>
                  </div>
                  <div class="text-muted">Total: ${{ formatCurrency(selectedBudget.total_amount) }}</div>
                </div>
                <div class="col-md-6 text-md-end">
                  <div class="text-success">Pagado: ${{ formatCurrency(selectedBudget.total_paid) }}</div>
                  <div v-if="selectedBudget.remaining_balance > 0" class="text-danger">
                    Pendiente: ${{ formatCurrency(selectedBudget.remaining_balance) }}
                  </div>
                  <div v-else class="text-success fw-bold">
                    Pago Completado
                  </div>
                </div>
              </div>
              <div class="progress mt-2" style="height: 10px;">
                <div class="progress-bar progress-bar-striped" role="progressbar"
                  :style="{ width: `${selectedBudget.payment_progress}%` }"
                  :class="getProgressBarColor(selectedBudget.payment_progress)"
                  :aria-valuenow="selectedBudget.payment_progress" aria-valuemin="0" aria-valuemax="100"></div>
              </div>
            </div>

            <div v-if="budgetPayments.length === 0" class="text-center py-4">
              <i class="bi bi-cash-stack display-4 text-muted mb-3"></i>
              <p class="text-muted mb-3">No hay pagos registrados para este presupuesto</p>
              <button class="btn btn-primary" @click="registerPaymentForBudget(selectedBudget)">
                <i class="bi bi-plus-circle me-1"></i> Registrar Pago
              </button>
            </div>
            <table v-else class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Método</th>
                  <th>Notas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="payment in budgetPayments" :key="payment.id">
                  <td>{{ formatDate(payment.payment_date) }}</td>
                  <td>${{ formatCurrency(payment.amount) }}</td>
                  <td>{{ getPaymentMethodLabel(payment.payment_method) }}</td>
                  <td>{{ payment.notes || '-' }}</td>
                  <td>
                    <button class="btn btn-sm btn-outline-secondary me-1" @click="editPaymentFromBudget(payment)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" @click="deletePaymentFromBudget(payment)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-light fw-bold">
                  <td>Total:</td>
                  <td>${{ formatCurrency(selectedBudget?.total_paid || 0) }}</td>
                  <td colspan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="registerPaymentForBudget(selectedBudget)"
              v-if="budgetPayments.length > 0 && selectedBudget?.remaining_balance > 0">
              <i class="bi bi-plus-circle me-1"></i> Registrar Pago
            </button>
            <button type="button" class="btn btn-secondary" @click="closeBudgetPaymentsModal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Budget Confirmation Modal -->
    <div class="modal fade" :class="{ 'show d-block': showDeleteBudgetModal }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar eliminación</h5>
            <button type="button" class="btn-close" @click="showDeleteBudgetModal = false"></button>
          </div>
          <div class="modal-body">
            <p>¿Está seguro que desea eliminar este presupuesto?</p>
            <div v-if="selectedBudget" class="alert alert-info">
              <div><strong>Nombre:</strong> {{ selectedBudget.nombre || `Presupuesto #${selectedBudget.id}` }}</div>
              <div v-if="selectedBudget.description"><strong>Descripción:</strong> {{ selectedBudget.description }}</div>
              <div><strong>Monto:</strong> ${{ formatCurrency(selectedBudget.total_amount) }}</div>
              <div><strong>Pagado:</strong> ${{ formatCurrency(selectedBudget.total_paid) }}</div>
            </div>
            <p class="text-danger">Esta acción no se puede deshacer y eliminará todos los pagos asociados.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteBudgetModal = false">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="deleteBudget" :disabled="deletingBudget">
              <span v-if="deletingBudget" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Modal backdrops for all modals -->
    <div
      v-if="showImageModalFlag || showPaymentModalFlag || showPaymentHistoryModalFlag || showDeletePaymentModalFlag || showEditPaymentModalFlag || showBudgetModalFlag || showBudgetPaymentsModal || showDeleteBudgetModal || showPatientInfoModalFlag"
      class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import { patientService, appointmentService, treatmentPlanService, clinicalHistoryService, odontogramService, paymentService, budgetService } from '@/services/api';
import OdontogramPreview from '@/components/odontogram/OdontogramPreview.vue';
import axios from 'axios';
import { formatDateForDisplay, calculateAge, formatTime, formatDateForAPI, formatDateForInput, getTodayForInput } from '@/utils/dateUtils';

export default {
  name: 'PatientDetail',
  components: {
    OdontogramPreview
  },
  data() {
    return {
      patient: {},
      clinicalHistory: null,
      odontogram: null,
      appointments: [],
      treatment_plans: [],
      loading: true,
      error: null,
      recentImage: null,
      showImageModalFlag: false,
      selectedImage: null,
      treatmentHistory: [],

      // Payment related data
      payments: [],
      totalPaid: 0,
      paymentProgress: 0,
      remainingBalance: 0,
      showPaymentModalFlag: false,
      showPaymentHistoryModalFlag: false,
      showDeletePaymentModalFlag: false,
      showEditPaymentModalFlag: false,
      newPayment: this.getEmptyPayment(),
      selectedPayment: null,
      editPayment: this.getEmptyPayment(),

      // Budget related
      budgets: [],
      showBudgetModalFlag: false,
      showBudgetPaymentsModal: false,
      showDeleteBudgetModal: false,
      budgetForm: {
        patient: null,
        nombre: '',
        total_amount: '',
        description: ''
      },
      budgetValidationErrors: {},
      savingBudget: false,
      editingBudget: false,
      selectedBudget: null,
      budgetPayments: [],
      deletingBudget: false,
      selectedBudgetId: null,
      paymentFilterBudgetId: 'all',
      showPatientInfoModalFlag: false,
    };
  },
  computed: {
    hasClinicalHistory() {
      return this.clinicalHistory !== null;
    },
    hasOdontogram() {
      return this.odontogram !== null;
    },
    hasTreatmentPlans() {
      return this.patient.treatment_plans && this.patient.treatment_plans.length > 0;
    },
    hasBudgets() {
      return this.patient.budgets && this.patient.budgets.length > 0;
    },
    filteredPayments() {
      if (this.paymentFilterBudgetId === 'all') {
        return this.payments;
      }
      return this.payments.filter(payment => payment.budget === parseInt(this.paymentFilterBudgetId));
    },
    hasAllergies() {
      if (!this.clinicalHistory || !this.clinicalHistory.notes) return false;
      try {
        const match = this.clinicalHistory.notes.match(/__CUSTOM_FIELDS__:\n([\s\S]*?)__END_CUSTOM_FIELDS__/);
        if (match && match[1]) {
          const customFields = JSON.parse(match[1].trim());
          return customFields.allergies && customFields.allergies.length > 0;
        }
      } catch (e) {
        console.error('Error parsing custom fields for allergies:', e);
      }
      return false;
    },
    hasRecentImage() {
      return this.recentImage !== null;
    }
  },
  async created() {
    const patientId = this.$route.params.id;
    try {
      await this.fetchPatient(patientId);
      await Promise.all([
        this.fetchClinicalHistory(patientId),
        this.fetchAppointments(patientId),
        this.fetchPayments(patientId),
        this.fetchBudgets(),
        this.fetchTreatmentPlans(patientId),
      ]);
      // Set initial selected budget if available
      if (this.hasBudgets) {
        this.selectedBudgetId = this.patient.budgets[0].id;
      }
    } catch (error) {
      console.error('Error loading patient details:', error);
      this.error = 'No se pudo cargar la información del paciente. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchPatient(patientId) {
      const response = await patientService.get(patientId);
      this.patient = response.data;
    },
    showPatientInfoModal() {
      this.showPatientInfoModalFlag = true;
    },
    closePatientInfoModal() {
      this.showPatientInfoModalFlag = false;
    },
    async fetchTreatmentPlans(patientId) {
      try {
        const response = await treatmentPlanService.getByPatient(patientId);
        this.treatment_plans = response.data;
        if (this.patient) {
          if (!this.patient.treatment_plans) {
            this.patient.treatment_plans = [];
          }
          this.patient.treatment_plans = [...this.treatment_plans];
        }
      } catch (error) {
        console.error('Error fetching treatment plans:', error);
      }
    },
    // Budget related methods
    async fetchBudgets() {
      try {
        const response = await budgetService.getByPatient(this.patient.id);
        this.budgets = response.data;
        if (!this.patient.budgets) {
          this.patient.budgets = this.budgets;
        }
      } catch (error) {
        console.error('Error fetching budgets:', error);
      }
    },
    showBudgetModal() {
      this.editingBudget = false;
      this.budgetForm = {
        patient: this.patient.id,
        nombre: '',
        total_amount: '',
        description: ''
      };
      this.budgetValidationErrors = {};
      this.showBudgetModalFlag = true;
    },
    editBudget(budget) {
      this.editingBudget = true;
      this.budgetForm = {
        id: budget.id,
        patient: this.patient.id,
        nombre: budget.nombre || '',
        total_amount: budget.total_amount,
        description: budget.description || ''
      };
      this.budgetValidationErrors = {};
      this.showBudgetModalFlag = true;
    },
    confirmDeleteBudget(budget) {
      this.selectedBudget = budget;
      this.showDeleteBudgetModal = true;
    },
    async deleteBudget() {
      if (!this.selectedBudget) return;
      this.deletingBudget = true;
      try {
        await budgetService.delete(this.selectedBudget.id);
        await this.fetchPatient(this.patient.id);
        await this.fetchBudgets();
        this.showDeleteBudgetModal = false;
        this.selectedBudget = null;
        alert('Presupuesto eliminado con éxito');
      } catch (error) {
        console.error('Error deleting budget:', error);
        alert('Error al eliminar el presupuesto');
      } finally {
        this.deletingBudget = false;
      }
    },
    closeBudgetModal() {
      this.showBudgetModalFlag = false;
      this.budgetForm = {
        patient: this.patient.id,
        nombre: '',
        total_amount: '',
        description: ''
      };
      this.budgetValidationErrors = {};
      this.editingBudget = false;
    },
    validateBudgetForm() {
      this.budgetValidationErrors = {};
      let isValid = true;
      if (!this.budgetForm.nombre) {
        this.budgetValidationErrors.nombre = 'El nombre del presupuesto es requerido';
        isValid = false;
      }
      if (!this.budgetForm.total_amount || parseFloat(this.budgetForm.total_amount) <= 0) {
        this.budgetValidationErrors.total_amount = 'El monto debe ser mayor que 0';
        isValid = false;
      }
      return isValid;
    },
    async saveBudget() {
      if (!this.validateBudgetForm()) return;
      this.savingBudget = true;
      try {
        const budgetData = { ...this.budgetForm };
        budgetData.total_amount = parseFloat(budgetData.total_amount);
        if (this.editingBudget) {
          await budgetService.update(budgetData.id, budgetData);
          alert('Presupuesto actualizado con éxito');
        } else {
          await budgetService.create(budgetData);
          alert('Presupuesto creado con éxito');
        }
        await this.fetchPatient(this.patient.id);
        await this.fetchBudgets();
        this.closeBudgetModal();
        if (this.patient.budgets.length === 1) {
          this.selectedBudgetId = this.patient.budgets[0].id;
        }
      } catch (error) {
        console.error('Error saving budget:', error);
        alert('Error al guardar el presupuesto');
      } finally {
        this.savingBudget = false;
      }
    },
    async showBudgetPayments(budget) {
      this.selectedBudget = budget;
      this.showBudgetPaymentsModal = true;
      try {
        const response = await paymentService.getByBudget(budget.id);
        this.budgetPayments = response.data.sort((a, b) =>
          new Date(b.payment_date) - new Date(a.payment_date)
        );
      } catch (error) {
        console.error('Error fetching budget payments:', error);
        this.budgetPayments = [];
      }
    },
    closeBudgetPaymentsModal() {
      this.showBudgetPaymentsModal = false;
      this.selectedBudget = null;
      this.budgetPayments = [];
    },
    registerPaymentForBudget(budget) {
      this.newPayment = this.getEmptyPayment();
      this.newPayment.patient = this.patient.id;
      this.newPayment.budget = budget.id;
      if (this.showBudgetPaymentsModal) {
        this.closeBudgetPaymentsModal();
      }
      this.selectedBudget = budget;
      this.showPaymentModalFlag = true;
    },
    editPaymentFromBudget(payment) {
      this.closeBudgetPaymentsModal();
      this.openEditPaymentModal(payment);
    },
    deletePaymentFromBudget(payment) {
      this.closeBudgetPaymentsModal();
      this.openDeletePaymentModal(payment);
    },
    onBudgetChange() {
      this.paymentFilterBudgetId = this.selectedBudgetId;
    },
    getBudgetDescription(budgetId) {
      if (!budgetId) return '-';
      const budget = this.patient.budgets ? this.patient.budgets.find(b => b.id === budgetId) : null;
      if (budget) {
        return budget.nombre || `Presupuesto #${budget.id}`;
      }
      return `Presupuesto #${budgetId}`;
    },
    getTotalFilteredPayments() {
      return this.filteredPayments.reduce((total, payment) => total + parseFloat(payment.amount), 0);
    },
    getProgressBarColor(progress) {
      if (progress < 25) return 'bg-danger';
      if (progress < 50) return 'bg-warning';
      if (progress < 75) return 'bg-info';
      return 'bg-success';
    },
    async fetchClinicalHistory(patientId) {
      try {
        const response = await clinicalHistoryService.getByPatient(patientId);
        if (response.data && response.data.length > 0) {
          this.clinicalHistory = response.data[0];
          this.parseTreatmentHistory();
          if (this.clinicalHistory) {
            await Promise.all([
              this.fetchOdontogram(this.clinicalHistory.id),
              this.fetchRecentImage(this.clinicalHistory.id)
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching clinical history:', error);
      }
    },
    parseTreatmentHistory() {
      this.treatmentHistory = [];
      if (this.clinicalHistory && this.clinicalHistory.notes) {
        let notesContent = this.clinicalHistory.notes;
        const treatmentsRegex = /\[\s*{"date":[^\]]*\]\s*/;
        const treatmentsMatch = notesContent.match(treatmentsRegex);
        if (treatmentsMatch && treatmentsMatch[0]) {
          try {
            this.treatmentHistory = JSON.parse(treatmentsMatch[0]);
            this.treatmentHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
            console.log('Successfully parsed treatment history:', this.treatmentHistory);
            notesContent = notesContent.replace(treatmentsRegex, '').trim();
          } catch (e) {
            console.error('Error parsing treatments:', e);
          }
        }
      }
    },
    async fetchOdontogram(clinicalHistoryId) {
      try {
        const response = await odontogramService.getByClinicalHistory(clinicalHistoryId);
        if (response.data && response.data.length > 0) {
          this.odontogram = response.data[0];
        }
      } catch (error) {
        console.error('Error fetching odontogram:', error);
      }
    },
    async fetchRecentImage(clinicalHistoryId) {
      try {
        const response = await axios.get(`/api/clinical-images/by_clinical_history/?clinical_history_id=${clinicalHistoryId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (response.data && response.data.length > 0) {
          const sortedImages = [...response.data].sort((a, b) =>
            new Date(b.upload_date) - new Date(a.upload_date)
          );
          this.recentImage = sortedImages[0];
        }
      } catch (error) {
        console.error('Error fetching recent image:', error);
        this.recentImage = null;
      }
    },
    async fetchAppointments(patientId) {
      try {
        const response = await appointmentService.getByPatient(patientId);
        this.appointments = response.data.sort((a, b) =>
          new Date(b.fecha) - new Date(a.fecha)
        );
      } catch (error) {
        console.error('Error fetching appointments:', error);
      }
    },
    async fetchPayments(patientId) {
      try {
        const response = await paymentService.getByPatient(patientId);
        this.payments = response.data.sort((a, b) =>
          new Date(b.payment_date) - new Date(a.payment_date)
        );
      } catch (error) {
        console.error('Error fetching payments:', error);
      }
    },
    parseHistoryPreview(notes) {
      if (!notes) return 'No hay notas clínicas registradas.';
      let cleanedNotes = notes.replace(/__CUSTOM_FIELDS__:[\s\S]*?__END_CUSTOM_FIELDS__/, '');
      cleanedNotes = cleanedNotes.replace(/\[\s*\{\s*"date"[\s\S]*?\}\s*\]/g, '');
      cleanedNotes = cleanedNotes.trim();
      if (cleanedNotes.length > 150) {
        cleanedNotes = cleanedNotes.substring(0, 147) + '...';
      }
      return cleanedNotes || 'No hay notas clínicas registradas.';
    },
    showOdontogram() {
      this.$router.push(`/odontograms/${this.odontogram.id}`);
    },
    editOdontogram() {
      this.$router.push(`/odontograms/${this.odontogram.id}/edit`);
    },
    createOdontogram() {
      if (this.clinicalHistory) {
        this.$router.push(`/odontograms/new?clinical_history=${this.clinicalHistory.id}`);
      } else {
        this.createClinicalHistory();
      }
    },
    showClinicalHistory() {
      this.$router.push(`/clinical-histories/${this.clinicalHistory.id}`);
    },
    viewTreatmentPlans() {
      if (this.hasTreatmentPlans && this.patient.treatment_plans.length > 0) {
        this.$router.push(`/treatment-plans/${this.patient.treatment_plans[0].id}`);
      } else {
        this.$router.push(`/treatment-plans?patient=${this.patient.id}`);
      }
    },
    createClinicalHistory() {
      this.$router.push(`/clinical-histories/new?patient=${this.patient.id}`);
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return `${first}${last}`;
    },
    formatDate(dateString) {
  return formatDateForDisplay(dateString);
},
    formatTime(timeString) {
      return formatTime(timeString);
    },
    
    formatAge(dateString) {
      return calculateAge(dateString);
    },
    getStatusClass(status) {
      const statusClasses = {
        'programada': 'bg-info',
        'completada': 'bg-success',
        'inasistencia': 'bg-danger',
        'confirmada': 'bg-warning'
      };
      return statusClasses[status] || 'bg-secondary';
    },
    getTreatmentStatusName(status) {
      const statuses = {
        'en_planificacion': 'En Planificación',
        'iniciado': 'Iniciado',
        'completado': 'Terminado',
        'en_pausa': 'En Pausa'
      };
      return statuses[status] || status;
    },
    getTreatmentStatusClass(status) {
      const statusClasses = {
        'en_planificacion': 'bg-info',
        'iniciado': 'bg-warning',
        'completado': 'bg-success',
        'en_pausa': 'bg-danger'
      };
      return statusClasses[status] || 'bg-info';
    },
    getImageTypeLabel(type) {
      const types = {
        'photo': 'Fotografía',
        'xray': 'Radiografía',
        'panoramic': 'Panorámica',
        'other': 'Otro'
      };
      return types[type] || 'Imagen';
    },
    showImageModal(image) {
      this.selectedImage = image;
      this.showImageModalFlag = true;
    },
    closeImageModal() {
      this.showImageModalFlag = false;
      this.selectedImage = null;
    },
    showAllImages() {
      this.closeImageModal();
      this.showClinicalHistory();
    },
    getEmptyPayment() {
  return {
    patient: null,
    budget: '',
    amount: '',
    payment_date: getTodayForInput(), // This now handles errors safely
    payment_method: '',
    notes: ''
  };
},
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.00';
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return '0.00';
      return numValue.toFixed(2);
    },
    getPaymentMethodLabel(method) {
      const methods = {
        'CASH': 'Efectivo',
        'CARD': 'Tarjeta',
        'TRANSFER': 'Transferencia',
        'CHECK': 'Cheque',
        'OTHER': 'Otro'
      };
      return methods[method] || method;
    },
    showPaymentModal() {
      this.newPayment = this.getEmptyPayment();
      this.newPayment.patient = this.patient.id;
      if (this.selectedBudget) {
        this.newPayment.budget = this.selectedBudget.id;
      } else if (this.hasBudgets) {
        this.newPayment.budget = this.patient.budgets[0].id;
      }
      this.showPaymentModalFlag = true;
    },
    closePaymentModal() {
      this.showPaymentModalFlag = false;
    },
    showPaymentHistoryModal() {
      if (this.selectedBudgetId) {
        this.paymentFilterBudgetId = this.selectedBudgetId;
      } else {
        this.paymentFilterBudgetId = 'all';
      }
      this.showPaymentHistoryModalFlag = true;
    },
    closePaymentHistoryModal() {
      this.showPaymentHistoryModalFlag = false;
    },
    openDeletePaymentModal(payment) {
      this.selectedPayment = payment;
      this.showDeletePaymentModalFlag = true;
    },
    closeDeletePaymentModal() {
      this.showDeletePaymentModalFlag = false;
      this.selectedPayment = null;
    },
    openEditPaymentModal(payment) {
      this.closePaymentHistoryModal();
      this.editPayment = {
        id: payment.id,
        patient: payment.patient,
        budget: payment.budget,
        amount: payment.amount,
        // Using formatDateForInput from dateUtils
        payment_date: formatDateForInput(payment.payment_date),
        payment_method: payment.payment_method,
        notes: payment.notes || ''
      };
      this.showEditPaymentModalFlag = true;
    },
    closeEditPaymentModal() {
      this.showEditPaymentModalFlag = false;
      this.editPayment = this.getEmptyPayment();
    },
    async savePayment() {
      try {
        if (!this.newPayment.amount || !this.newPayment.payment_date ||
          !this.newPayment.payment_method || !this.newPayment.budget) {
          alert('Por favor complete todos los campos requeridos');
          return;
        }
        this.newPayment.amount = parseFloat(this.newPayment.amount);
        if (isNaN(this.newPayment.amount) || this.newPayment.amount <= 0) {
          alert('Por favor ingrese un monto válido');
          return;
        }
        await paymentService.create(this.newPayment);
        alert('Pago registrado con éxito');
        await this.fetchPatient(this.patient.id);
        await this.fetchPayments(this.patient.id);
        await this.fetchBudgets();
        this.closePaymentModal();
      } catch (error) {
        console.error('Error saving payment:', error);
        alert('Error al guardar el pago');
      }
    },
    async deletePayment() {
      try {
        if (!this.selectedPayment) return;
        await paymentService.delete(this.selectedPayment.id);
        alert('Pago eliminado con éxito');
        await this.fetchPatient(this.patient.id);
        await this.fetchPayments(this.patient.id);
        await this.fetchBudgets();
        this.closeDeletePaymentModal();
      } catch (error) {
        console.error('Error deleting payment:', error);
        alert('Error al eliminar el pago');
      }
    },
    async updatePayment() {
      try {
        if (!this.editPayment.amount || !this.editPayment.payment_date ||
          !this.editPayment.payment_method || !this.editPayment.budget) {
          alert('Por favor complete todos los campos requeridos');
          return;
        }
        this.editPayment.amount = parseFloat(this.editPayment.amount);
        if (isNaN(this.editPayment.amount) || this.editPayment.amount <= 0) {
          alert('Por favor ingrese un monto válido');
          return;
        }
        await paymentService.update(this.editPayment.id, this.editPayment);
        alert('Pago actualizado con éxito');
        await this.fetchPatient(this.patient.id);
        await this.fetchPayments(this.patient.id);
        await this.fetchBudgets();
        this.closeEditPaymentModal();
      } catch (error) {
        console.error('Error updating payment:', error);
        alert('Error al actualizar el pago');
      }
    },
    // Método para dividir el texto en líneas sin filtrar las vacías
    getTextLines(text) {
      if (!text) return [];
      return text.split(/\r?\n/);
    }
  }
};

</script>

<style scoped>
.avatar-large {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: bold;
  color: #495057;
}

.avatar-medium {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: #495057;
}

.patient-info {
  margin-top: 1.5rem;
}

.info-item {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.info-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.info-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.info-value {
  font-size: 1rem;
}

.odontogram-preview {
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

img {
  cursor: pointer;
  transition: transform 0.2s ease;
}

img:hover {
  transform: scale(1.05);
}

.budget-summary {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}

.progress {
  background-color: #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  transition: width 0.5s ease;
}

.treatment-timeline {
  position: relative;
  padding-left: 2rem;
}

.treatment-timeline::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 7px;
  width: 2px;
  background-color: #e9ecef;
}

.treatment-item {
  position: relative;
  margin-bottom: 1.5rem;
}

.treatment-item:last-child {
  margin-bottom: 0;
}

.treatment-date {
  font-weight: 500;
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.treatment-date::before {
  content: '';
  position: absolute;
  top: 5px;
  left: -2rem;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #0d6efd;
}

.treatment-content {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border-left: 3px solid #0d6efd;
}

.budget-description {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  padding: 0;
  overflow: hidden;
}

.budget-description-text {
  font-family: inherit;
  margin: 0;
  padding: 0.75rem;
  background-color: transparent;
  border: none;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-line;
  color: #212529;
  overflow-wrap: break-word;
}

.treatment-plan-item {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  border-left: 3px solid #6f42c1;
}

.treatment-plan-description {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  padding: 0;
  overflow: hidden;
  margin-top: 0.5rem;
}

.treatment-plan-description-text {
  font-family: inherit;
  margin: 0;
  padding: 0.75rem;
  background-color: transparent;
  border: none;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-line;
  color: #212529;
  overflow-wrap: break-word;
}

.modal.show {
  display: block;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
}

.patient-info-section {
  margin-bottom: 1.5rem;
}

.section-title {
  position: relative;
  font-weight: 600;
  padding-bottom: 0.75rem;
  margin-bottom: 1rem;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
}

.patient-info-item {
  display: flex;
  margin-bottom: 1.25rem;
  align-items: flex-start;
}

.info-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  background-color: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  color: #6c757d;
}

.info-details {
  flex: 1;
}

.info-label {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.info-value {
  font-weight: 500;
  word-break: break-word;
}

.notes-container {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  border-left: 3px solid #6c757d;
  padding: 0;
  overflow: hidden;
}

.notes-content {
  font-family: inherit;
  margin: 0;
  padding: 1rem;
  background-color: transparent;
  border: none;
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-line;
  color: #212529;
  overflow-wrap: break-word;
  width: 100%;
}
</style>
